import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConsultingConfig } from '@jordan/types';
import { Alert, Breadcrumb, Button, Input, PageHeader, SelectMenu, SettingsPanel, SettingsRow, Spinner } from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { apiGet, apiPut } from '../lib/api';

export function ConsultingSettingsPage() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState<ConsultingConfig | null>(null);
  const [selected, setSelected] = useState('');
  const [maxHours, setMaxHours] = useState('168');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<ConsultingConfig>('/api/config/consulting')
      .then((c) => {
        setCfg(c);
        setSelected(c.defaultCalendarId ?? '');
        setMaxHours(String(c.maxWorkHours));
      })
      .catch(() => setError('Failed to load Billing & Time settings.'));
  }, []);

  const save = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await apiPut<ConsultingConfig>('/api/config/consulting', {
        defaultCalendarId: selected || null,
        maxWorkHours: Number(maxHours) || 168,
      });
      setCfg(saved);
      setSelected(saved.defaultCalendarId ?? '');
      setMaxHours(String(saved.maxWorkHours));
      setMessage('Billing & Time settings saved.');
    } catch {
      setError('Could not save — check the values (max work hours 1–168).');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">Billing &amp; Time</span>
          </>
        }
        breadcrumb={
          <Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Configuration' }, { label: 'Billing & Time' }]} />
        }
      />

      <div className="content">
        {message && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {error}
          </Alert>
        )}

        {!cfg ? (
          <Spinner />
        ) : (
          <SettingsPanel
            title="Billing & Time"
            description="Defaults for the Consulting billing application."
          >
            <SettingsRow
              title="Default calendar"
              description="The calendar the billing app books and reads time against by default. Pick from your connected, writable calendars."
            >
              {cfg.calendars.length === 0 ? (
                <div className="text-muted">
                  No connected calendars yet. Connect one under{' '}
                  <a
                    href="/configuration/connections"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/configuration/connections');
                    }}
                  >
                    Connections
                  </a>
                  .
                </div>
              ) : (
                <div className="d-flex align-items-start gap-2" style={{ maxWidth: 520 }}>
                  <div className="flex-grow-1">
                    <SelectMenu
                      className="mb-0"
                      icon="ph-calendar-dots"
                      value={selected}
                      onChange={setSelected}
                      options={[
                        { value: '', label: 'None' },
                        ...cfg.calendars.map((c) => ({ value: c.id, label: c.summary })),
                      ]}
                    />
                  </div>
                  <Button variant="primary" disabled={busy} onClick={save}>
                    Save
                  </Button>
                </div>
              )}
            </SettingsRow>

            <SettingsRow
              title="Maximum weekly work hours"
              description="The largest fixed weekly work-hours value allowed for a team member (7 × 24 = 168)."
            >
              <div className="d-flex align-items-start gap-2" style={{ maxWidth: 320 }}>
                <div style={{ width: 140 }}>
                  <Input
                    className="mb-0"
                    type="number"
                    min={1}
                    max={168}
                    step={1}
                    value={maxHours}
                    onChange={(e) => setMaxHours(e.target.value)}
                  />
                </div>
                <Button variant="primary" disabled={busy} onClick={save}>
                  Save
                </Button>
              </div>
            </SettingsRow>
          </SettingsPanel>
        )}
      </div>
    </AppChrome>
  );
}
